import React, { useState, useEffect } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './CBA.css';

const CBA = () => {
    const { t } = useLanguage();
    const [cbas, setCbas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [selectedCba, setSelectedCba] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        sector: ''
    });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        employer_party: '',
        union_party: '',
        signed_date: '',
        effective_date: '',
        expiry_date: '',
        status: 'draft',
        sector: '',
        branch_id: '',
        negotiation_history: ''
    });

    useEffect(() => {
        loadCbas();
    }, [filters]);

    const loadCbas = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.sector) params.append('sector', filters.sector);

            const response = await api.request(`/cba?${params.toString()}`);
            setCbas(response.data || []);
        } catch (error) {
            console.error('Failed to load CBAs:', error);
            setCbas([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const method = selectedCba ? 'PUT' : 'POST';
            const url = selectedCba ? `/cba/${selectedCba.id}` : '/cba';

            const response = await api.request(url, {
                method,
                body: JSON.stringify(formData)
            });

            setShowForm(false);
            resetForm();
            loadCbas();
        } catch (error) {
            console.error('Failed to save CBA:', error);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            employer_party: '',
            union_party: '',
            signed_date: '',
            effective_date: '',
            expiry_date: '',
            status: 'draft',
            sector: '',
            branch_id: '',
            negotiation_history: ''
        });
        setSelectedCba(null);
    };

    const handleEdit = (cba) => {
        setSelectedCba(cba);
        setFormData({
            title: cba.title || '',
            description: cba.description || '',
            employer_party: cba.employer_party || '',
            union_party: cba.union_party || '',
            signed_date: cba.signed_date || '',
            effective_date: cba.effective_date || '',
            expiry_date: cba.expiry_date || '',
            status: cba.status || 'draft',
            sector: cba.sector || '',
            branch_id: cba.branch_id || '',
            negotiation_history: cba.negotiation_history || ''
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Je, una uhakika unataka kufuta mkataba huu wa CBA?'))) {
            return;
        }

        try {
            await api.request(`/cba/${id}`, { method: 'DELETE' });
            loadCbas();
        } catch (error) {
            console.error('Failed to delete CBA:', error);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            draft: 'badge-secondary',
            active: 'badge-success',
            expired: 'badge-warning',
            terminated: 'badge-danger'
        };
        return badges[status] || 'badge-secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('sw-TZ');
    };

    const isExpiringSoon = (expiryDate) => {
        if (!expiryDate) return false;
        const expiry = new Date(expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
    };

    return (
        <div className="cba-page">
            <div className="page-header">
                <div className="header-content">
                    <h1 className="page-title">{t('Mikakati ya Mazungumzo ya Pamoja (CBA)')}</h1>
                    <p className="page-subtitle">{t('Dhibiti na fuatilia mikataba ya mazungumzo ya pamoja')}</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => setShowForm(true)}
                >
                    {t('Ongeza Mkataba Mpya')}
                </button>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-group">
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({...filters, status: e.target.value})}
                        className="form-control"
                    >
                        <option value="">{t('Hali Zote')}</option>
                        <option value="draft">{t('Rasimu')}</option>
                        <option value="active">{t('Hai')}</option>
                        <option value="expired">{t('Imemalizika')}</option>
                        <option value="terminated">{t('Imekatizwa')}</option>
                    </select>
                </div>
                <div className="filter-group">
                    <input
                        type="text"
                        placeholder={t('Tafuta kwa sekta...')}
                        value={filters.sector}
                        onChange={(e) => setFilters({...filters, sector: e.target.value})}
                        className="form-control"
                    />
                </div>
            </div>

            {/* CBAs List */}
            <div className="cba-list">
                {loading ? (
                    <div className="loading-spinner"></div>
                ) : cbas.length > 0 ? (
                    cbas.map(cba => (
                        <div key={cba.id} className="cba-card">
                            <div className="cba-header">
                                <div className="cba-title-section">
                                    <h3 className="cba-title">{cba.title}</h3>
                                    <span className="cba-number">#{cba.cba_number}</span>
                                </div>
                                <div className="cba-badges">
                                    <span className={`badge ${getStatusBadge(cba.status)}`}>
                                        {cba.status}
                                    </span>
                                    {isExpiringSoon(cba.expiry_date) && (
                                        <span className="badge badge-warning">
                                            {t('Inamalizika Hivi Karibuni')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="cba-body">
                                <div className="cba-parties">
                                    <div className="party-info">
                                        <strong>{t('Mwajiri:')}</strong> {cba.employer_party}
                                    </div>
                                    <div className="party-info">
                                        <strong>{t('Chama:')}</strong> {cba.union_party}
                                    </div>
                                </div>
                                <div className="cba-dates">
                                    <div className="date-info">
                                        <strong>{t('Kusainiwa:')}</strong> {formatDate(cba.signed_date)}
                                    </div>
                                    <div className="date-info">
                                        <strong>{t('Kuanza:')}</strong> {formatDate(cba.effective_date)}
                                    </div>
                                    <div className="date-info">
                                        <strong>{t('Malizika:')}</strong> {formatDate(cba.expiry_date)}
                                    </div>
                                </div>
                                {cba.sector && (
                                    <div className="cba-meta">
                                        <strong>{t('Sekta:')}</strong> {cba.sector}
                                    </div>
                                )}
                                {cba.description && (
                                    <p className="cba-description">{cba.description}</p>
                                )}
                            </div>
                            <div className="cba-actions">
                                <button
                                    className="btn btn-sm btn-info"
                                    onClick={() => handleEdit(cba)}
                                >
                                    {t('Hariri')}
                                </button>
                                {cba.document_path && (
                                    <button className="btn btn-sm btn-secondary">
                                        {t('Pakua Hati')}
                                    </button>
                                )}
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(cba.id)}
                                >
                                    {t('Futa')}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="empty-state">
                        <h3>{t('Hakuna mikataba ya CBA iliyopatikana')}</h3>
                        <p>{t('Kama kuna mkataba wa CBA, tafadhali ongeza hapa.')}</p>
                    </div>
                )}
            </div>

            {/* New/Edit CBA Modal */}
            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>{selectedCba ? t('Hariri Mkataba wa CBA') : t('Ongeza Mkataba Mpya wa CBA')}</h3>
                            <button
                                className="modal-close"
                                onClick={() => { setShowForm(false); resetForm(); }}
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Kichwa cha Mkataba')}</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('Hali')}</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        <option value="draft">{t('Rasimu')}</option>
                                        <option value="active">{t('Hai')}</option>
                                        <option value="expired">{t('Imemalizika')}</option>
                                        <option value="terminated">{t('Imekatizwa')}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Jina la Mwajiri')}</label>
                                    <input
                                        type="text"
                                        value={formData.employer_party}
                                        onChange={(e) => setFormData({...formData, employer_party: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('Jina la Chama cha Wafanyakazi')}</label>
                                    <input
                                        type="text"
                                        value={formData.union_party}
                                        onChange={(e) => setFormData({...formData, union_party: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('Tarehe ya Kusainiwa')}</label>
                                    <input
                                        type="date"
                                        value={formData.signed_date}
                                        onChange={(e) => setFormData({...formData, signed_date: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('Tarehe ya Kuanza')}</label>
                                    <input
                                        type="date"
                                        value={formData.effective_date}
                                        onChange={(e) => setFormData({...formData, effective_date: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('Tarehe ya Kumalizika')}</label>
                                    <input
                                        type="date"
                                        value={formData.expiry_date}
                                        onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('Sekta')}</label>
                                <input
                                    type="text"
                                    value={formData.sector}
                                    onChange={(e) => setFormData({...formData, sector: e.target.value})}
                                    placeholder={t('Mfano: Viwanda, Kilimo, Elimu')}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('Maelezo')}</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('Historia ya Mazungumzo (Hiari)')}</label>
                                <textarea
                                    value={formData.negotiation_history}
                                    onChange={(e) => setFormData({...formData, negotiation_history: e.target.value})}
                                    rows={4}
                                    placeholder={t('Eleza historia ya mazungumzo...')}
                                />
                            </div>

                            <div className="form-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); resetForm(); }}>
                                    {t('Ghairi')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {selectedCba ? t('Hifadhi Mabadiliko') : t('Ongeza Mkataba')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CBA;
